import { Paths } from "@app/routes";
import {
  AppContentCard,
  AppContentHeader,
  AppContentToolbar,
  type BreadcrumbType,
  WithPermission,
} from "@atlas/composite";
import {
  Button,
  cn,
  IrmMountainIcon,
  MainSidebar,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  Separator,
  type SidebarItemData,
  SidebarProvider,
  SidebarTrigger,
  Typography,
  WithToolTip,
} from "@atlas/core";
import {
  AppMode,
  handleLogout,
  usePermission,
  useSessionData,
  useTranslation,
  useZodSearchParams,
} from "@atlas/sdk";
import {
  useHubCurrentCart,
  useHubPackage,
  useLogout,
  useUserInfo,
} from "@query";
import {
  APP_BASE_PATH,
  defaultHubCartPackageId,
  HUB_PERM,
  useBreadcrumbs,
} from "@shared";
import { useQueryClient } from "@tanstack/react-query";
import { cva } from "class-variance-authority";
import {
  BarChart3,
  Database,
  FolderOpen,
  Headset,
  Home as HomeIcon,
  KeyIcon,
  LayoutGrid,
  LogOut,
  Package,
  ReceiptText,
  Settings,
  ShieldCheck,
  Store,
  UserCircle,
} from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { z } from "zod";
import { useReleaseVersion } from "../hooks/useReleaseVersion";
import { HubCartButton, HubCartPanel } from "./HubCart";

const rootVariants = cva("dxp-hub-app flex h-screen w-full bg-sidebar");

const searchParamsSchema = z.object({
  exclusive: z.string().optional(),
});

function getActiveContentId(pathname: string): string {
  const firstSegment = pathname.replace(/^\//, "").split("/")[0] || Paths.Home;
  const mapping: Record<string, string> = {
    "": Paths.Home,
    [Paths.Home]: Paths.Home,
    [Paths.CatalogManager]: Paths.CatalogManager,
    [Paths.PackageManager]: Paths.PackageManager,
    [Paths.CatalogTypeManager]: Paths.CatalogTypeManager,
    [Paths.Administer]: Paths.Administer,
    [Paths.Subscription]: Paths.Subscription,
    [Paths.ExportList]: Paths.ExportList,
    [Paths.CdvManager]: Paths.CdvManager,
    [Paths.Settings]: Paths.Settings,
    [Paths.MyProfile]: Paths.MyProfile,
    [Paths.SupportCenter]: Paths.SupportCenter,
    [Paths.Dashboard]: Paths.Dashboard,
  };
  return mapping[firstSegment] ?? Paths.Home;
}

function getInitials(firstName: string, lastName: string): string {
  const first = firstName?.trim().charAt(0) ?? "";
  const last = lastName?.trim().charAt(0) ?? "";
  return (first + last).toUpperCase() || "U";
}

type ShellViewAsRoleToolbarProps = {
  isLoading: boolean;
  children: ReactNode;
};

export function Shell({ isLoading, children }: ShellViewAsRoleToolbarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const breadcrumbs = useBreadcrumbs();
  const activeContentId = getActiveContentId(location.pathname);
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const isHomePage =
    pathSegments.length === 0 ||
    (pathSegments.length === 1 && pathSegments[0] === Paths.Home);
  const isDashboardRoute = activeContentId === Paths.Dashboard;
  const releaseVersion = useReleaseVersion(isHomePage);
  const { data: userInfo } = useUserInfo(APP_BASE_PATH);

  const { exclusive } = useZodSearchParams(searchParamsSchema);
  const isExclusive = exclusive === "true";

  const { isPermission: hasCatalogView } = usePermission([
    HUB_PERM.CATALOG_VIEW,
  ]);
  const { isPermission: hasCatalogTypeManage } = usePermission([
    HUB_PERM.CATALOG_TYPE_MANAGE,
  ]);
  const { isPermission: hasAdministerAccess } = usePermission([
    HUB_PERM.ADMINISTER_ACCESS,
  ]);
  const { isPermission: hasDashboardAccess } = usePermission([
    HUB_PERM.DASHBOARD_ACCESS,
  ]);
  const { isPermission: hasCatalogBuy } = usePermission([HUB_PERM.CATALOG_BUY]);
  const { isPermission: hasSubscriptionManage } = usePermission([
    HUB_PERM.SUBSCRIPTION_MANAGE,
  ]);
  const { isPermission: hasCdvManage } = usePermission([HUB_PERM.CDV_MANAGE]);
  const { isPermission: hasPackageTransfer } = usePermission([
    HUB_PERM.PACKAGE_TRANSFER,
  ]);
  const { isPermission: hasCatalogTransfer } = usePermission([
    HUB_PERM.CATALOG_TRANSFER,
  ]);

  const hasSubscriptionNav = hasCatalogBuy || hasSubscriptionManage;
  const hasExportList = hasPackageTransfer && hasCatalogTransfer;

  const firstSettingsChildPath = useMemo(() => {
    if (hasAdministerAccess) return Paths.Administer;
    if (hasSubscriptionNav) return Paths.Subscription;
    if (hasExportList) return Paths.ExportList;
    if (hasCdvManage) return Paths.CdvManager;
    return null;
  }, [hasAdministerAccess, hasSubscriptionNav, hasExportList, hasCdvManage]);

  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();
  const { data: userHubCart } = useHubCurrentCart(uid, {
    enabled: uid.length > 0 && !isLoading && hasCatalogBuy,
  });
  const hubCartPackageId = useMemo(
    () => defaultHubCartPackageId(userHubCart),
    [userHubCart],
  );
  const { data: headerCartPackage, isSuccess: headerCartPackageQuerySuccess } =
    useHubPackage(hubCartPackageId, {
      enabled: !isLoading && hasCatalogBuy && hubCartPackageId > 0,
    });
  const packageCartCount = useMemo(
    () => headerCartPackage?.catalogRelations?.length ?? 0,
    [headerCartPackage?.catalogRelations],
  );

  const prevPackageCountRef = useRef<number | null>(null);
  const prevPackageIdRef = useRef<number | null>(null);
  const hubPackagesInitialSyncRef = useRef(false);
  const [headerCartBump, setHeaderCartBump] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    if (!hasCatalogBuy) {
      hubPackagesInitialSyncRef.current = false;
      prevPackageCountRef.current = null;
      prevPackageIdRef.current = null;
    }
  }, [hasCatalogBuy]);

  useEffect(() => {
    if (!hasCatalogBuy) {
      return;
    }
    if (hubCartPackageId > 0 && !headerCartPackageQuerySuccess) {
      return;
    }
    if (!hubPackagesInitialSyncRef.current) {
      hubPackagesInitialSyncRef.current = true;
      prevPackageCountRef.current = packageCartCount;
      prevPackageIdRef.current = hubCartPackageId;
      return;
    }
    const prevCount = prevPackageCountRef.current ?? 0;
    const prevId = prevPackageIdRef.current;

    let shouldBump = false;

    if (packageCartCount > prevCount) {
      shouldBump = true;
      setIsCartOpen(true);
    }

    if (prevId != null && prevId !== hubCartPackageId && hubCartPackageId > 0) {
      shouldBump = true;
      setIsCartOpen(true);
    }

    if (shouldBump) {
      setHeaderCartBump(true);
    }

    prevPackageCountRef.current = packageCartCount;
    prevPackageIdRef.current = hubCartPackageId;
  }, [
    hasCatalogBuy,
    headerCartPackageQuerySuccess,
    packageCartCount,
    hubCartPackageId,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isCartPage =
      location.pathname.includes(`/${Paths.PackageManager}`) &&
      !params.has("view") &&
      !params.has("tab") &&
      !params.has("deploymentId") &&
      (!params.has("packageId") || params.get("fromTab") === "packages");

    if (isCartPage && isCartOpen) {
      setIsCartOpen(false);
    }
  }, [location.pathname, location.search, isCartOpen]);

  const sidebarMenuItems = useMemo((): SidebarItemData[] => {
    const items: SidebarItemData[] = [
      {
        id: Paths.Home,
        label: t("hub.sidebar.home"),
        icon: <HomeIcon className={"size-4"} />,
      },
    ];

    if (hasDashboardAccess) {
      items.push({
        id: Paths.Dashboard,
        label: t("hub.sidebar.dashboard", {
          defaultValue: "Dashboard",
        }),
        icon: <BarChart3 className={"size-4"} />,
      });
    }

    const catalogChildren: SidebarItemData[] = [];
    if (hasCatalogView) {
      catalogChildren.push({
        id: Paths.CatalogManager,
        label: t("hub.sidebar.catalogs"),
        icon: <LayoutGrid className={"size-4"} />,
      });
    }
    if (hasCatalogTypeManage) {
      catalogChildren.push({
        id: Paths.CatalogTypeManager,
        label: t("hub.sidebar.catalog_types"),
        icon: <FolderOpen className={"size-4"} />,
      });
    }
    if (catalogChildren.length > 0) {
      items.push({
        id: Paths.CatalogManagement,
        label: t("hub.sidebar.catalog_manager"),
        icon: <Store className={"size-4"} />,
        children: catalogChildren,
      });
    }

    if (hasCatalogBuy) {
      items.push({
        id: Paths.PackageManager,
        label: t("hub.sidebar.package_manager"),
        icon: <Package className={"size-4"} />,
      });
    }

    const settingsChildren: SidebarItemData[] = [];
    if (hasAdministerAccess) {
      settingsChildren.push({
        id: Paths.Administer,
        label: t("hub.sidebar.administer"),
        icon: <ShieldCheck className={"size-4"} />,
      });
    }
    if (hasSubscriptionNav) {
      settingsChildren.push({
        id: Paths.Subscription,
        label: t("hub.home.solution_subscription_status"),
        icon: <ReceiptText className={"size-4"} />,
      });
    }
    if (hasExportList) {
      settingsChildren.push({
        id: Paths.ExportList,
        label: t("hub.sidebar.token_manager", {
          defaultValue: "Token Manager",
        }),
        icon: <KeyIcon className={"size-4"} />,
      });
    }
    if (hasCdvManage) {
      settingsChildren.push({
        id: Paths.CdvManager,
        label: t("hub.sidebar.cdvs", {
          defaultValue: "Configuration values",
        }),
        icon: <Database className={"size-4"} />,
      });
    }
    if (settingsChildren.length > 0) {
      items.push({
        id: Paths.SettingsMenu,
        label: t("hub.sidebar.settings"),
        icon: <Settings className={"size-4"} />,
        children: settingsChildren,
      });
    }

    return items;
  }, [
    t,
    hasCatalogView,
    hasCatalogTypeManage,
    hasAdministerAccess,
    hasDashboardAccess,
    hasSubscriptionNav,
    hasCatalogBuy,
    hasCdvManage,
    hasExportList,
  ]);

  const userData = useMemo(() => {
    return {
      name: userInfo
        ? `${userInfo.firstName ?? ""} ${userInfo.lastName ?? ""}`.trim() ||
          userInfo.loginId ||
          t("hub.sidebar.user")
        : t("hub.sidebar.user"),
      initials: userInfo
        ? getInitials(userInfo.firstName ?? "", userInfo.lastName ?? "")
        : "U",
      email: userInfo?.email ?? "user@atlas.local",
    };
  }, [userInfo, t]);

  const logoutMutation = useLogout({
    onSettled: () => {
      handleLogout();
      void queryClient.clear();
      window.location.href = APP_BASE_PATH || "/";
    },
  });

  const handleSidebarLogout = useCallback(() => {
    logoutMutation.mutate();
  }, [logoutMutation]);

  const footerPopover = useMemo(
    () => (
      <div className="flex flex-col gap-1 p-2">
        <Button asChild variant="ghost" className="w-full justify-start gap-2">
          <Link to={`/${Paths.MyProfile}`}>
            <UserCircle className={"size-4 shrink-0"} />
            {t("hub.sidebar.my_profile")}
          </Link>
        </Button>

        <Button
          type="button"
          variant="ghost"
          className="w-full justify-start gap-2"
          disabled={logoutMutation.isPending}
          onClick={handleSidebarLogout}>
          <LogOut className={"size-4 shrink-0"} />
          {t("hub.sidebar.log_out")}
        </Button>
      </div>
    ),
    [t, handleSidebarLogout, logoutMutation.isPending],
  );

  return (
    <SidebarProvider>
      <div className={cn(rootVariants())}>
        <MainSidebar
          menuConfig={sidebarMenuItems}
          headerConfig={{
            label: t("hub.sidebar.dxp_hub"),
            logo: <IrmMountainIcon />,
          }}
          userData={userData}
          footerPopover={footerPopover}
          activeContentId={activeContentId}
          isExclusive={isExclusive}
          onContentSelect={(item: SidebarItemData) => {
            const navigateTo = (path: string) => {
              navigate(path);
            };
            if (item.id === Paths.Home) {
              navigateTo(`/${Paths.Home}`);
              return;
            }
            if (item.id === Paths.CatalogManagement) {
              navigateTo(`/${Paths.CatalogManager}`);
              return;
            }
            if (item.id === Paths.SettingsMenu) {
              if (firstSettingsChildPath) {
                navigateTo(`/${firstSettingsChildPath}`);
              }
              return;
            }
            if (item.id === Paths.PackageManager) {
              navigateTo(
                `/${Paths.PackageManager}?tab=${encodeURIComponent(Paths.PackageManagerViewPackages)}`,
              );
              return;
            }
            navigateTo(`/${item.id}`);
          }}
        />

        <AppContentCard
          appMode={AppMode.Portal}
          isLoading={isLoading}
          breadcrumbs={breadcrumbs as BreadcrumbType[]}>
          <AppContentHeader>
            <SidebarTrigger />
            <Separator
              orientation="vertical"
              decorative
              className="h-4 ml-1 mr-2 self-center"
            />
          </AppContentHeader>
          <AppContentToolbar>
            {isHomePage && (
              <Typography.Muted className="order-first mb-0 shrink-0 whitespace-nowrap text-xs pr-4">
                Release Version : {releaseVersion}
              </Typography.Muted>
            )}
            <WithToolTip
              tooltipText={t("hub.routes.support_center", {
                defaultValue: "Support Center",
              })}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/${Paths.SupportCenter}`)}
                title={t("hub.routes.support_center", {
                  defaultValue: "Support Center",
                })}>
                <Headset className="size-5" />
              </Button>
            </WithToolTip>
            <WithPermission permissions={[HUB_PERM.CATALOG_BUY]}>
              <HubCartButton
                packageCartCount={packageCartCount}
                headerCartBump={headerCartBump}
                onAnimationEnd={() => setHeaderCartBump(false)}
                isOpen={isCartOpen}
                onToggle={() => setIsCartOpen(!isCartOpen)}
              />
            </WithPermission>
          </AppContentToolbar>
          <ResizablePanelGroup direction="horizontal" className="h-full w-full">
            <ResizablePanel
              defaultSize={100}
              minSize={30}
              className={cn(
                "flex flex-col min-h-0 min-w-0",
                isDashboardRoute ? "p-0" : "p-4",
              )}>
              {children}
            </ResizablePanel>
            {isCartOpen && (
              <>
                <ResizableHandle withHandle={true} />
                <ResizablePanel
                  defaultSize={30}
                  minSize={30}
                  maxSize={50}
                  className="flex flex-col min-h-0 min-w-0 border-l border-border">
                  <HubCartPanel onClose={() => setIsCartOpen(false)} />
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
        </AppContentCard>
      </div>
    </SidebarProvider>
  );
}
